import { promises as fs } from 'fs';
import path from 'path';
import type { WorkspaceOverlay } from '@whittlelabs/loom';

/**
 * Materialize a `WorkspaceOverlay` into an already-prepared run directory:
 * fetch each referenced file and write it in place, so whatever runs in the
 * workspace can read it as an ordinary file. This is how a producer supplies
 * out-of-band content (team standards, a rendered diff, reference docs) by
 * reference — the content never travels through the job payload.
 *
 * Three guards, because the file list comes from the (potentially untrusted)
 * job spec and this runs on a shared host:
 *  1. **Host allowlist** — the executor's OWN config declares which origins it
 *     will fetch from (never the spec), so a spec can only point at the
 *     producer's own service. A URL off the allowlist is skipped, not fetched.
 *  2. **Path containment** — the destination is resolved against the run dir
 *     and must stay inside it; `..`, absolute paths, and symlink escapes are
 *     rejected.
 *  3. **Size cap** — a per-file byte ceiling so a hostile endpoint can't fill
 *     the disk.
 *
 * Every failure is best-effort and non-fatal: a skipped file is reported in
 * the result, the run continues (the model simply won't see that file).
 */
export interface ApplyOverlayOptions {
  /** Origins (`https://host[:port]`) the executor may fetch overlay files from. */
  allowedHosts: string[];
  signal?: AbortSignal;
  /** Per-fetch budget. */
  timeoutMs?: number;
  /** Per-file byte ceiling. */
  maxBytes?: number;
}

export interface OverlayResult {
  written: number;
  /**
   * Relative paths of the files actually written. Read attribution uses this to
   * credit an overlaid file as read when it's opened via the shell (`cat`,
   * `grep`) rather than the Read tool, which the Read-only telemetry misses.
   */
  writtenPaths: string[];
  skipped: Array<{ path: string; reason: string }>;
}

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024; // 5 MiB/file — standards docs are text

export async function applyOverlay(
  runDir: string,
  overlay: WorkspaceOverlay,
  options: ApplyOverlayOptions,
): Promise<OverlayResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const maxBytes = options.maxBytes ?? DEFAULT_MAX_BYTES;
  const allowed = new Set(options.allowedHosts.map(normalizeOrigin).filter((o): o is string => !!o));
  const rootReal = await fs.realpath(runDir);

  const result: OverlayResult = { written: 0, writtenPaths: [], skipped: [] };
  const files = Array.isArray(overlay.files) ? overlay.files : [];

  for (const file of files) {
    const skip = (reason: string) => result.skipped.push({ path: String(file?.path ?? '?'), reason });

    if (typeof file?.path !== 'string' || typeof file?.url !== 'string') {
      skip('malformed overlay entry (path/url must be strings)');
      continue;
    }

    const dest = safeDestination(rootReal, file.path);
    if (!dest) {
      skip('path escapes the workspace root');
      continue;
    }

    let origin: string | undefined;
    try {
      const u = new URL(file.url);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') {
        skip(`unsupported url protocol ${u.protocol}`);
        continue;
      }
      origin = normalizeOrigin(u.origin);
    } catch {
      skip('invalid url');
      continue;
    }
    if (!origin || !allowed.has(origin)) {
      skip(`origin not in the overlay allowlist (${origin})`);
      continue;
    }

    try {
      const content = await fetchCapped(file.url, { timeoutMs, maxBytes, signal: options.signal });
      await fs.mkdir(path.dirname(dest), { recursive: true });
      await fs.writeFile(dest, content);
      result.written += 1;
      result.writtenPaths.push(file.path);
    } catch (err) {
      skip(err instanceof Error ? err.message : String(err));
    }
  }

  return result;
}

/**
 * Resolve `relPath` under `rootReal` and return the absolute destination iff it
 * stays inside the root. Rejects absolute paths and any `..` traversal. Because
 * we only ever mkdir+write (never follow an existing symlink into the tree),
 * containment is by prefix on the resolved path.
 */
function safeDestination(rootReal: string, relPath: string): string | undefined {
  if (relPath.length === 0 || path.isAbsolute(relPath)) return undefined;
  const resolved = path.resolve(rootReal, relPath);
  const rootWithSep = rootReal.endsWith(path.sep) ? rootReal : rootReal + path.sep;
  if (resolved !== rootReal && !resolved.startsWith(rootWithSep)) return undefined;
  // Belt and suspenders: reject any `..` segment outright.
  if (relPath.split(/[/\\]/).includes('..')) return undefined;
  return resolved;
}

/** `https://host:port` with the default port dropped; undefined if unparseable. */
function normalizeOrigin(value: string): string | undefined {
  try {
    const u = new URL(value.includes('://') ? value : `https://${value}`);
    return u.origin;
  } catch {
    return undefined;
  }
}

/**
 * Fetch a URL, aborting on timeout / caller signal and enforcing a byte cap
 * while streaming so an oversized (or chunked, header-lying) body can't blow
 * memory or disk.
 */
async function fetchCapped(
  url: string,
  opts: { timeoutMs: number; maxBytes: number; signal?: AbortSignal },
): Promise<Buffer> {
  const controller = new AbortController();
  const onParentAbort = () => controller.abort();
  opts.signal?.addEventListener('abort', onParentAbort, { once: true });
  const timer = setTimeout(() => controller.abort(), opts.timeoutMs);
  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw new Error(`overlay fetch returned HTTP ${response.status}`);
    }
    if (!response.body) {
      const buf = Buffer.from(await response.arrayBuffer());
      if (buf.byteLength > opts.maxBytes) throw new Error('overlay file exceeds size cap');
      return buf;
    }
    const chunks: Buffer[] = [];
    let total = 0;
    for await (const chunk of response.body as unknown as AsyncIterable<Uint8Array>) {
      const buf = Buffer.from(chunk);
      total += buf.byteLength;
      if (total > opts.maxBytes) throw new Error('overlay file exceeds size cap');
      chunks.push(buf);
    }
    return Buffer.concat(chunks);
  } finally {
    clearTimeout(timer);
    opts.signal?.removeEventListener('abort', onParentAbort);
  }
}
